<script>
// Keeps the native option API while adding one keyboard-accessible visual vocabulary.
export default {
  inheritAttrs: false,
  props: { value: { type: [String, Number, Boolean], default: '' }, disabled: Boolean, maxPanelHeight: { type: Number, default: 320 } },
  data() {
    return { open: false, active: 0, opensUp: false, panelHeight: 320, query: '', alignRight: false }
  },
  computed: {
    options: {
      cache: false,
      get() {
        return (this.$slots.default || [])
          .filter(node => node.tag === 'option')
          .map(node => ({
            value:
              node.data && node.data.domProps && node.data.domProps.value !== undefined
                ? node.data.domProps.value
                : node.data && node.data.attrs
                  ? node.data.attrs.value
                  : '',
            label: (node.children || []).map(child => child.text || '').join(''),
            disabled: !!(
              node.data &&
              node.data.attrs &&
              Object.prototype.hasOwnProperty.call(node.data.attrs, 'disabled') &&
              node.data.attrs.disabled !== false
            )
          }))
      }
    },
    // Long lists get a search box and a wider panel, so names stay on one line.
    searchable() {
      return this.options.length > 8
    },
    shown: {
      cache: false,
      get() {
        const query = this.query.trim().toLocaleLowerCase()
        return query ? this.options.filter(option => option.label.toLocaleLowerCase().includes(query)) : this.options
      }
    },
    chosen: {
      cache: false,
      get() {
        return (
          this.options.find(option => String(option.value) === String(this.value)) ||
          this.options[0] || { label: '' }
        )
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.outside)
    document.addEventListener('tc-menu-open', this.other)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.outside)
    document.removeEventListener('tc-menu-open', this.other)
  },
  methods: {
    outside(event) {
      if (!this.$el.contains(event.target)) this.open = false
    },
    other(event) {
      if (event.detail !== this._uid) this.open = false
    },
    toggle() {
      if (this.disabled) return
      this.open = !this.open
      this.query = ''
      if (this.open) {
        const bounds = this.$refs.trigger.getBoundingClientRect()
        this.alignRight = this.searchable && bounds.left + 380 > window.innerWidth - 16
        const below = window.innerHeight - bounds.bottom - 16
        const above = bounds.top - 16
        this.opensUp = below < Math.min(this.maxPanelHeight, this.options.length * 44 + 14) && above > below
        this.panelHeight = Math.max(80, Math.min(this.maxPanelHeight, this.opensUp ? above : below))
        document.dispatchEvent(new CustomEvent('tc-menu-open', { detail: this._uid }))
        this.active = Math.max(
          0,
          this.options.findIndex(option => String(option.value) === String(this.value))
        )
        if (this.searchable) this.$nextTick(() => this.$refs.search && this.$refs.search.focus({ preventScroll: true }))
      }
    },
    choose(option) {
      if (!option || option.disabled) return
      this.$emit('input', option.value)
      this.$emit('change', { target: { value: option.value } })
      this.open = false
      this.$refs.trigger.focus({ preventScroll: true })
    },
    key(event) {
      const list = this.shown
      if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Escape'].includes(event.key)) {
        if (event.target === this.$refs.search && ['Home', 'End'].includes(event.key)) return
        event.preventDefault()
        if (event.key === 'Escape') {
          this.open = false
          this.$refs.trigger.focus({ preventScroll: true })
          return
        }
        if (!this.open) this.toggle()
        else {
          this.active =
            event.key === 'Home'
              ? 0
              : event.key === 'End'
                ? list.length - 1
                : (this.active + (event.key === 'ArrowDown' ? 1 : -1) + list.length) %
                Math.max(1, list.length)
        }
        this.$nextTick(() => {
          const el = this.$el.querySelector('[data-active="true"]')
          if (el) {
            const panel = el.parentElement
            if (el.offsetTop < panel.scrollTop) panel.scrollTop = el.offsetTop
            else if (el.offsetTop + el.offsetHeight > panel.scrollTop + panel.clientHeight) panel.scrollTop = el.offsetTop + el.offsetHeight - panel.clientHeight
          }
        })
      } else if (this.open && (event.key === 'Enter' || (event.key === ' ' && event.target !== this.$refs.search))) {
        event.preventDefault()
        this.choose(list[this.active])
      } else if (event.key === 'Tab') this.open = false
    }
  },
  render(h) {
    return h('div', { class: ['tc-v2-select', { 'is-open': this.open }], on: { keydown: this.key } }, [
      h(
        'button',
        {
          ref: 'trigger',
          class: 'tc-v2-select-trigger',
          attrs: {
            ...this.$attrs,
            type: 'button',
            disabled: this.disabled,
            role: 'combobox',
            'aria-haspopup': 'listbox',
            'aria-expanded': String(this.open),
            'aria-controls': `select-${this._uid}`,
            'aria-activedescendant': this.open ? `option-${this._uid}-${this.active}` : undefined
          },
          on: { click: this.toggle }
        },
        [h('span', { class: 'tc-v2-select-label', attrs: { title: this.chosen.label } }, this.chosen.label), h('svg', { class: 'tc-v2-select-chevron', attrs: { 'aria-hidden': 'true', width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' } }, [h('path', { attrs: { d: 'm6 9 6 6 6-6' } })])]
      ),
      this.open
        ? h(
          'div',
          { class: ['tc-v2-select-panel', { 'opens-up': this.opensUp, 'is-searchable': this.searchable, 'aligns-right': this.alignRight }], style: { maxHeight: `${this.panelHeight}px` } },
          [
            this.searchable
              ? h('input', {
                ref: 'search',
                class: 'tc-v2-select-search',
                attrs: { type: 'search', placeholder: 'Search', 'aria-label': 'Search options', autocomplete: 'off' },
                domProps: { value: this.query },
                on: { input: event => { this.query = event.target.value; this.active = 0 } }
              })
              : null,
            h('div', { class: 'tc-v2-select-list', attrs: { id: `select-${this._uid}`, role: 'listbox' } }, [
              ...this.shown.map((option, index) =>
                h(
                  'button',
                  {
                    key: String(option.value),
                    class: 'tc-v2-select-option',
                    attrs: {
                      id: `option-${this._uid}-${index}`,
                      type: 'button',
                      role: 'option',
                      tabindex: -1,
                      disabled: option.disabled,
                      'aria-selected': String(String(option.value) === String(this.value)),
                      'data-active': String(index === this.active)
                    },
                    on: { click: () => this.choose(option) }
                  },
                  [h('span', { attrs: { title: option.label } }, option.label), String(option.value) === String(this.value) ? h('span', '✓') : null]
                )
              ),
              this.shown.length ? null : h('p', { class: 'tc-v2-select-empty' }, 'No matches')
            ])
          ]
        )
        : null
    ])
  }
}
</script>
<style>
.tc-v2-select { position:relative; padding:0!important; border:0!important; background:none!important; min-width:0; }
.tc-v2-select-trigger { display:flex; align-items:center; justify-content:space-between; gap:16px; text-align:left; height:48px; width:100%; padding:0 14px; border:1px solid #E6E3DC; border-radius:8px; background:white; color:#14203A; font:inherit; font-size:15px; }
.tc-v2-select-label { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.tc-v2-select-chevron { flex:none; }
.tc-v2-select.is-open .tc-v2-select-trigger { border-color:#1B1C40; box-shadow:none; }
.tc-v2-select-panel { position:absolute; top:calc(100% + 6px); left:0; width:100%; max-height:320px; overflow:auto; background:white; border:1px solid #E6E3DC; border-radius:12px; box-shadow:0 12px 32px rgba(20,32,58,.14); padding:6px; z-index:50; }
.tc-v2-select-option { display:flex; justify-content:space-between; align-items:center; gap:20px; width:100%; min-height:44px; padding:8px 12px; white-space:normal; overflow-wrap:anywhere; border-radius:8px; text-align:left; font-size:15px; color:#14203A; }
.tc-v2-select-panel.is-searchable { display:flex; flex-direction:column; width:max(100%, 380px); max-width:calc(100vw - 32px); overflow:hidden; }
.tc-v2-select-panel.aligns-right { left:auto; right:0; }
.tc-v2-select-panel .tc-v2-select-list { overflow:auto; min-height:0; }
.tc-v2-select-panel.is-searchable .tc-v2-select-option { white-space:nowrap; }
.tc-v2-select-panel.is-searchable .tc-v2-select-option span:first-child { min-width:0; overflow:hidden; text-overflow:ellipsis; }
.tc-v2-select-search { flex:none; width:100%; height:40px; margin:0 0 6px; padding:0 12px; border:1px solid #E6E3DC; border-radius:8px; font:inherit; font-size:15px; color:#14203A; }
.tc-v2-select-search:focus { outline:none; border-color:#1B1C40; }
.tc-v2-select-empty { padding:10px 12px; font-size:14px; color:#5A6478; }
.tc-v2-select-option:hover,.tc-v2-select-option[data-active=true] { background:#F8F7F3; }
</style>
